import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";

import { updateTenant } from "@/services/authService";
import type { TenantCurrentResponse } from "@/services/authService";
import type { OwnerPostCertViewSteps } from "@/pages/onboarding/ownerPostCertSteps";

interface UseCfeImportStepParams {
  tenant: TenantCurrentResponse | null;
  viewStep: number;
  ownerPostCertViewSteps: OwnerPostCertViewSteps;
  needsCfeImportStep: boolean;
  setViewStep: Dispatch<SetStateAction<number>>;
  loadTenant: () => Promise<void>;
  t: TFunction;
}

/**
 * Owns the CFE-import onboarding step: the "at least one import done" flag
 * (cleared when returning to the CAE step, set when the tenant has already
 * completed it), the skip handler, and the mark-completed callback the import
 * component calls after a successful upload.
 */
export function useCfeImportStep({
  tenant,
  viewStep,
  ownerPostCertViewSteps,
  needsCfeImportStep,
  setViewStep,
  loadTenant,
  t,
}: UseCfeImportStepParams) {
  /** CFE onboarding step: Next enabled only after at least one successful import; cleared when returning to CAE. */
  const [cfeImportUploadCompleted, setCfeImportUploadCompleted] =
    useState(false);

  useEffect(() => {
    if (!needsCfeImportStep || ownerPostCertViewSteps.cfe == null) return;
    if (viewStep === ownerPostCertViewSteps.cae) {
      setCfeImportUploadCompleted(false);
    }
  }, [
    viewStep,
    needsCfeImportStep,
    ownerPostCertViewSteps.cae,
    ownerPostCertViewSteps.cfe,
  ]);

  useEffect(() => {
    if (!needsCfeImportStep || ownerPostCertViewSteps.cfe == null) return;
    if (tenant?.onboardingCfeImportStepCompletedAt) {
      setCfeImportUploadCompleted(true);
    }
  }, [
    tenant?.onboardingCfeImportStepCompletedAt,
    needsCfeImportStep,
    ownerPostCertViewSteps.cfe,
  ]);

  const handleSkipCfeImportStep = useCallback(async () => {
    if (!needsCfeImportStep || ownerPostCertViewSteps.cfe == null) return;
    try {
      await updateTenant({
        onboardingCfeImportStepCompletedAt: new Date().toISOString(),
      });
      await loadTenant();
      // Fiscal profile removed — skip directly to literalE / personalize.
      setViewStep(
        ownerPostCertViewSteps.literalE !== null
          ? ownerPostCertViewSteps.literalE
          : ownerPostCertViewSteps.personalize,
      );
    } catch {
      toast.error(t("onboarding.errorSave"));
    }
  }, [needsCfeImportStep, ownerPostCertViewSteps, loadTenant, setViewStep, t]);

  const markCfeImportUploadCompleted = useCallback(() => {
    setCfeImportUploadCompleted(true);
  }, []);

  return {
    cfeImportUploadCompleted,
    handleSkipCfeImportStep,
    markCfeImportUploadCompleted,
  };
}
